import isBoolean from 'lodash/isBoolean';
import {NextRouter, useRouter} from 'next/router';
import {useCallback, useMemo, useState} from 'react';
import Url, {qs} from 'url-parse';
import {StaticLandingGenreMoreFetcher} from '../../../../common/fetchers/StaticLandingGenreMoreFetcher';
import {t} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {requestContext} from '../../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../../common/shared/utils/constants';
import useTypedSelector from '../../../../common/useTypedSelector';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {SubTabItemType} from '../../../../design-system/organisms/SubTabItem/SubTabItemType.interface';
import {StaticLandingGenre as StaticLandingGenrePage} from '../../../../design-system/pages/StaticLanding/StaticLandingGenre/StaticLandingGenre';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../../domain/landing/constant';
import {StaticLandingGenreSection} from '../../../../graphql/generated/types';
import {selectIsSubtab} from '../../../../staticLanding/selector';
import {CommonSectionProps} from '../../type';

export function StaticLandingGenre({section, children}: CommonSectionProps) {
  const isSubtab = useTypedSelector(state => selectIsSubtab({section, state}));
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const router = useRouter();

  const [isShowSortTypeDialog, setIsShowSortTypeDialog] = useState(false);
  const staticLandingGenreSection = section as Omit<
    StaticLandingGenreSection,
    'groups'
  >;

  const {
    title,
    sortTypeList,
    subcategoryList: _subcategoryList,
    param,
    displayAd,
    totalCount,
    promotionBanner,
  } = staticLandingGenreSection;

  const subcategoryList: SubTabItemType[] = useMemo(
    () =>
      _subcategoryList.map(({name, param: subcategoryUid}) => {
        return {
          name,
          param: subcategoryUid,
          href: getSubCategoryUrl({
            isSubtab,
            categoryUid: param.categoryUid,
            router,
            subcategoryUid,
          }),
        };
      }),
    [_subcategoryList, isSubtab, param, router]
  );

  const onClickSortTypeItem = useCallback(
    (sortType: string) => {
      setIsShowSortTypeDialog(false);
      if (sortType !== param.sortType?.param) {
        router.push(
          {
            pathname: location.pathname,
            query: {
              ...qs.parse(location.search),
              ...getQueryString({sortType}),
            },
          },
          undefined,
          {scroll: false}
        );
      }
    },
    [param, router]
  );
  const onClickCompletedFilter = useCallback(() => {
    router.push({
      pathname: location.pathname,
      query: {
        ...qs.parse(location.search),
        ...getQueryString({isComplete: !param.isComplete}),
      },
    });
  }, [param, router]);
  return (
    <StaticLandingGenrePage
      isMobile={isMobile}
      isWebView={isWebView}
      isSubtab={isSubtab}
      title={title || undefined}
      totalCount={totalCount}
      sortTypeList={sortTypeList}
      subcategoryList={subcategoryList}
      currentSubcategoryUid={param.subcategory?.param}
      currentSortType={param.sortType?.param}
      isShowSortTypeDialog={isShowSortTypeDialog}
      onClickSortTypeTabItem={onClickSortTypeItem}
      showSortTypeDialog={() => setIsShowSortTypeDialog(true)}
      hideSortTypeDialog={() => setIsShowSortTypeDialog(false)}
      displayAd={displayAd}
      promotionBanner={promotionBanner}
      isComplete={!!param.isComplete}
      onClickCompletedFilter={onClickCompletedFilter}
    >
      {isGreaterThanZero(totalCount) && (
        <StaticLandingGenreMoreFetcher section={staticLandingGenreSection}>
          <div className="flex grow flex-col">{children}</div>
        </StaticLandingGenreMoreFetcher>
      )}
      {!isGreaterThanZero(totalCount) && (
        <NoItem isMobile={isMobile}>{t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}</NoItem>
      )}
    </StaticLandingGenrePage>
  );
}

function getSubCategoryUrl({
  isSubtab,
  router,
  categoryUid,
  subcategoryUid,
}: {
  isSubtab: boolean;
  router: NextRouter;
  categoryUid: number;
  subcategoryUid?: string;
}) {
  if (isSubtab) {
    // /menu/{menu_uid}/screen/{screen_uid}
    const pathname = IS_CLIENT_SIDE
      ? window.location.pathname
      : new Url(router.asPath).pathname;
    return `${pathname}?${
      subcategoryUid ? qs.stringify({subcategory_uid: subcategoryUid}) : ''
    }`;
  } else {
    return `/landing/genre/${categoryUid}${
      subcategoryUid !== undefined && `/${subcategoryUid}`
    }`;
  }
}

function getQueryString({
  sortType,
  isComplete,
}: {
  sortType?: string;
  isComplete?: boolean;
}) {
  return {
    ...(sortType && {sort_opt: sortType}),
    ...(isBoolean(isComplete) && {is_complete: isComplete}),
  };
}
